// People, sessions and API keys. There is no open sign-up: admins create accounts with a temporary password.
import { z } from "zod";

export const Role = z.enum(["admin", "user"]);
export type Role = z.infer<typeof Role>;

export const USERNAME_PATTERN = /^[a-z0-9][a-z0-9._-]{1,31}$/;

export const Username = z
  .string()
  .trim()
  .toLowerCase()
  .regex(USERNAME_PATTERN, "Usernames have 2–32 lowercase letters, digits, dots, dashes or underscores.");
export const Password = z
  .string()
  .min(8, "Passwords need at least 8 characters.")
  .max(200, "Passwords can have at most 200 characters.");
export const DisplayName = z
  .string()
  .trim()
  .min(1, "Enter a name.")
  .max(60, "Names can have at most 60 characters.");

export const User = z.object({
  id: z.string(),
  username: z.string(),
  displayName: z.string(),
  role: Role,
  disabled: z.boolean(),
  /** Signed in with a temporary password: the UI should ask for a new one. */
  mustChangePassword: z.boolean(),
  createdAt: z.string(),
  lastSeenAt: z.string().nullable(),
});
export type User = z.infer<typeof User>;

/** GET /api/me */
export const Me = z.object({
  user: User,
  /** How this request was authenticated. */
  via: z.enum(["session", "api_key"]),
});
export type Me = z.infer<typeof Me>;

/** GET /api/auth/status (public): whether the first admin still has to be created. */
export const AuthStatus = z.object({
  setupRequired: z.boolean(),
  signedIn: z.boolean(),
});
export type AuthStatus = z.infer<typeof AuthStatus>;

export const LoginBody = z.object({
  username: z.string().trim().toLowerCase().min(1),
  password: z.string().min(1),
});
export type LoginBody = z.infer<typeof LoginBody>;

/** POST /api/auth/setup: only while there are no users at all. */
export const SetupBody = z.object({
  username: Username,
  password: Password,
  displayName: DisplayName.optional(),
});
export type SetupBody = z.infer<typeof SetupBody>;

export const ChangePasswordBody = z.object({ currentPassword: z.string().min(1), newPassword: Password });
export type ChangePasswordBody = z.infer<typeof ChangePasswordBody>;

export const UpdateMeBody = z.object({ displayName: DisplayName });
export type UpdateMeBody = z.infer<typeof UpdateMeBody>;

/** Admin: new account. Without a password the server makes a temporary one (returned once). */
export const CreateUserBody = z.object({
  username: Username,
  displayName: DisplayName.optional(),
  role: Role.default("user"),
  password: Password.optional(),
});
export type CreateUserBody = z.infer<typeof CreateUserBody>;
export type CreateUserInput = z.input<typeof CreateUserBody>;

/** Admin: edit an account. `resetPassword` makes a new temporary password and signs the person out everywhere. */
export const UpdateUserBody = z.object({
  displayName: DisplayName.optional(),
  role: Role.optional(),
  disabled: z.boolean().optional(),
  resetPassword: z.boolean().optional(),
});
export type UpdateUserBody = z.infer<typeof UpdateUserBody>;

/** Answer to create / update user: the temporary password is shown once (null when none was made). */
export const UserWithPassword = z.object({ user: User, temporaryPassword: z.string().nullable() });
export type UserWithPassword = z.infer<typeof UserWithPassword>;

export const ApiKey = z.object({
  id: z.string(),
  name: z.string(),
  /** First characters of the key, to recognise it ("sk_studio_ab12…"). */
  prefix: z.string(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
});
export type ApiKey = z.infer<typeof ApiKey>;

export const CreateApiKeyBody = z.object({ name: z.string().trim().min(1, "Name the key.").max(60) });
export type CreateApiKeyBody = z.infer<typeof CreateApiKeyBody>;

/** The secret is returned only here; send it as `Authorization: Bearer <secret>`. */
export const CreatedApiKey = z.object({ apiKey: ApiKey, secret: z.string() });
export type CreatedApiKey = z.infer<typeof CreatedApiKey>;

/** Public face of a person, for queues and runner reservations. */
export const UserRef = z.object({ id: z.string(), displayName: z.string() });
export type UserRef = z.infer<typeof UserRef>;
